import { useState, useEffect } from "react"
import { useGetActiveDownloadRequestsMutation, useRequestDownloadFormsMutation, useGetDownloadLinksMutation } from "./formApiSlice"
import "./FormRequest.css"
import { Alert } from "antd"
import { selectCrm } from "../crm/crmSlice"
import { useSelector, useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"
import { selectCurrentId } from "../auth/authSlice"
import { FileTextOutlined, PaperClipOutlined } from "@ant-design/icons"
import { showSpinner, hideSpinner } from "../../components/componentSlice"

const FormRequest = () => {
    const crm = useSelector(selectCrm)
    const userId = useSelector(selectCurrentId)
    const navigate = useNavigate()
    const dispatch = useDispatch()

    const [activeRequests, setActiveRequests] = useState([])
    const [errorMessage, setErrorMessage] = useState(null)
    const [requestDownloadForms, { isLoading }] = useRequestDownloadFormsMutation()
    const [getActiveDownloadRequests, {isLoading: isGetActiveRequestsLoading}] = useGetActiveDownloadRequestsMutation()
    const [getDownloadableLinks, {isLoading: isGetDownloadLinksLoading}] = useGetDownloadLinksMutation()

    useEffect(() => {
        async function handleGetActiveRequests() {
            try {
                let request = {
                    crmId: crm,
                    userId: userId
                }
                const response = await getActiveDownloadRequests(request).unwrap()
                if(response) {
                    setActiveRequests(response)
                }
            } catch(err) {
                if(err.status == 403) {
                    navigate("/dash/error403")
                } else {
                    setErrorMessage(err.data?.detail)
                }
            }
        }
        
        handleGetActiveRequests()
    }, [])

    useEffect(() => {
        if(isLoading || isGetDownloadLinksLoading || isGetActiveRequestsLoading) {
            dispatch(showSpinner())
        } else{
            dispatch(hideSpinner())
        }
    }, [isLoading, isGetDownloadLinksLoading, isGetActiveRequestsLoading])

    const handleDownloadForms = async () => {
        setErrorMessage(null)

        try {
            let request = {
                crmId: crm,
                userId: userId,
            }
            let response = await requestDownloadForms(request).unwrap()
            if(response) {
                setActiveRequests(prev => [...prev, response])
            }
        } catch(err) {
            setErrorMessage(err.data?.detail)
        }
    }

    const handleDownloadableLinks = async (id) => {
        try {
            setErrorMessage(null)

            const response = await getDownloadableLinks(id).unwrap()
            if(response) {

                let updatedActiveRequests = activeRequests.map(request => {
                    if (request.id !== id) {
                        return request
                    }

                    let updated = {...request, 
                        downloadLinks: response
                    }

                    return updated
                })

                setActiveRequests(updatedActiveRequests)
            }
        } catch(err) {
            setErrorMessage(err.data?.detail)
        }
    }

    const getDaysLeftFromTimestamp = (timestampMs) => {
        const daysToExpire = 5

        const now = new Date()
        const targetDate = new Date(timestampMs + daysToExpire * 24 * 60 * 60 * 1000)
        const diffMs = targetDate - now

        let daysLeft = diffMs / (1000 * 60 * 60 * 24)
        return Math.floor(daysLeft)
    }

    const getPendingRequests = () => {
        let pendingRequests = activeRequests.filter(request => request.status === "REQUESTED" || request.status === "FORMS_READY")
        return (
            pendingRequests && pendingRequests.length > 0 ?
                pendingRequests.map(request => {
                    return (
                        <div className="forms-request-card">
                            <div className="forms-request-header">
                                <div className="forms-request-id">
                                    <p>Form Request Id: {request.id}</p>
                                </div>
                            </div>
                            <div className="forms-request-body">
                                <div>
                                    <p><strong>Status: </strong> {request.status === "REQUESTED" ? "Request Submitted" : "Waiting for Approval"}</p>
                                </div>
                            </div>

                            {
                                request?.status === "REQUESTED" ?
                                    <Alert
                                        description="Form request has been submitted and is being processed."
                                        type="success"
                                        showIcon
                                        banner
                                    />
                                :
                                    <Alert
                                        description="There's an on-going request created. Once approved, you can view the forms in Completed Requests."
                                        type="success"
                                        showIcon
                                        banner
                                    />
                            }
                        </div>
                    )
                })
            :
                <div className="enable-request-container">
                    <Alert
                        message="Once the request is approved, you can view the forms in Completed Requests."
                        type="info"
                        showIcon
                    />
                    <div className="forms-request-button">
                        <button onClick={() => handleDownloadForms()}>
                            <span>Request Forms</span>
                            <FileTextOutlined  />
                        </button>
                    </div>
                </div>
                
                
        ) 
    }

    const getCompletedRequests = () => {
        return (
            activeRequests.filter(request => request.status === "APPROVED").map(request => {
                return (
                    <div className="forms-request-card">
                        <div className="forms-request-header">
                            <div className="forms-request-id">
                                <p>Form Request Id: {request.id}</p>
                            </div>
                            {
                                !request.downloadLinks &&
                                    <div className="forms-review-button">
                                        <button button onClick={() => handleDownloadableLinks(request.id)}>View Forms</button>
                                    </div>
                            } 
                        </div>
                        <div className="forms-request-body">
                            <div>
                                <p><strong>Status:</strong> Approved</p>
                            </div>
                        </div>

                        <Alert description={`This will expire in ${getDaysLeftFromTimestamp(request.lastUpdatedDate)} day(s).`} banner />
                        {request.downloadLinks?.length > 0 &&
                            createDownloadableLinksForm(request.id, request.downloadLinks)
                        }
                    </div>
                )
            })
        )
    }

    const getRejectedRequests = () => {
        return (
            activeRequests.filter(request => request.status === "REJECTED").map(request => {
                return (
                    <div className="forms-request-card">
                        <div className="forms-request-header">
                            <div className="forms-request-id">
                                <p>Form Request Id: {request.id}</p>
                            </div>
                        </div>
                        <div className="forms-request-body">
                            <div>
                                <p><strong>Status:</strong> Rejected</p>
                            </div>
                        </div>

                        <Alert description={`This will expire in ${getDaysLeftFromTimestamp(request.lastUpdatedDate)} day(s).`} banner />
                    </div>
                )
            })
        )
    }

    const createDownloadableLinksForm = (id, downloadLinks) => {
        return (
            <div className="forms-review">
                <div className="review-instruction"><p>Click to download the files</p></div>
                <div className="downloadable-links">
                    {
                        downloadLinks.sort().map((download) => {
                            return (
                                <a href={download.link} target='_blank'>
                                    <button className="form-download">
                                        <PaperClipOutlined /><p>{`${download.fileName}.zip`}</p>
                                    </button>
                                </a>
                            )
                        })
                    }
                </div>
            </div>
        )
    }

    return (
        <div className="table-page-container">
            {errorMessage && 
                <div className="support-error">
                    <p>{errorMessage}</p>
                </div>
            }
            <div className="form-request-header">
                <div className='top-header'>
                    <header>Pending Request</header>
                </div>
            </div>
            <div className="pending-request-container">
                <div className="forms-request-container">
                    {getPendingRequests()}
                </div>
            </div>

            <div className="form-request-header">
                <div className='top-header'>
                    <header>Completed Requests</header>
                </div>
            </div>

            <div className="forms-request-container">
                {getCompletedRequests()}
            </div>

            <div className="form-request-header">
                <div className='top-header'>
                    <header>Rejected Requests</header>
                </div>
            </div>

            <div className="forms-request-container">
                {getRejectedRequests()}
            </div>
        </div>
    )
}

export default FormRequest